import type { BattleActor } from '@mezha/api'
import type { Pose } from './atlas'
import type { SceneEvent } from './model'

/** Use the saved starting state until each publicly confirmed status event occurs. */
export function actorAt(before: BattleActor, after: BattleActor, events: SceneEvent[], t: number) {
  if (t < 0 || t >= 2.05) return after
  let statuses = before.statuses
  for (const e of events.filter(e => e.target === after.id && e.at <= t).sort((a, b) => a.at - b.at)) {
    if (!e.status || e.status.result !== 'applied') continue
    const { id, remove } = e.status
    statuses = statuses.filter(s => s.id !== id)
    const confirmed = after.statuses.find(s => s.id === id)
    if (!remove && confirmed) statuses = [...statuses, confirmed]
  }
  return { ...before, statuses }
}

export function travelFactor(actor: BattleActor) {
  if (actor.statuses.some(s => s.id === 'pinned')) return 0
  if (actor.statuses.some(s => s.id === 'trip')) return .3
  return actor.statuses.some(s => s.id === 'hobbled') ? .55 : 1
}

type StagePoint = { x: number; y: number; size: number }

export const meleeReach = (family: string | undefined) => family === 'spear' ? .75 : family === 'staff' ? .65 : .6

/** Ready stances are already within one short step of contact, even on a wide arena. */
export function duelDistance(a: StagePoint, b: StagePoint, reach: number) {
  return Math.min(Math.abs(b.x - a.x), (a.size + b.size) * reach + Math.min(96, (a.size + b.size) * .18))
}

/** Plant, strike, hold contact briefly, then step back; no continuous half-second glide. */
export function meleeProgress(dt: number) {
  const smooth = (n: number) => { const x = Math.max(0, Math.min(1, n)); return x * x * (3 - 2 * x) }
  return dt < 0 ? smooth((dt + .16) / .16) : 1 - smooth((dt - .1) / .32)
}

/** Follow the actual target lane and leave room for the drawn weapon at contact. */
export function meleePosition(source: StagePoint, target: StagePoint, phase: number, reach: number, meeting: boolean, mobility: number, partnerMobility = 1) {
  const dx = target.x - source.x, dy = target.y - source.y
  const distance = Math.hypot(dx, dy)
  const stop = (source.size + target.size) * reach
  const share = mobility / Math.max(1, mobility + (meeting ? partnerMobility : 0))
  const fraction = distance > 0 ? Math.max(0, 1 - stop / distance) * share * Math.max(0, Math.min(1, phase)) : 0
  return { x: source.x + dx * fraction, y: source.y + dy * fraction }
}

export function impactHeight(zone: string | undefined) {
  return zone === 'head' ? .75 : zone === 'legs' ? .18 : .45
}

/** Pose and incoming recoil are separate channels: a simultaneous hit cannot cancel an attack. */
export function motionAt(actor: BattleActor, events: SceneEvent[], t: number, reduced = false) {
  const attacks = events.filter(e => e.actor === actor.id && e.attack && t >= e.at - (e.attack.family === 'bow' ? .65 : .45) && t < e.at + .5).sort((a, b) => a.at - b.at)
  // Later wind-ups cannot replace the contact of an earlier hit in a multi-hit action.
  const outgoing = attacks.filter(e => e.at <= t).at(-1) ?? attacks[0]
  const incoming = events.filter(e => e.target === actor.id && e.attack && t >= e.at && t < e.at + .32).sort((a, b) => a.at - b.at).at(-1)
  let pose: Pose = 'idle'
  if (actor.out) pose = actor.out_reason === 'forfeit' ? 'rest' : 'out'
  else if (reduced) pose = actor.statuses.some(s => s.id === 'stun' || s.id === 'trip') ? 'stagger' : 'idle'
  else if (outgoing) {
    const dt = t - outgoing.at
    pose = outgoing.attack?.family === 'bow' ? dt < -.4 ? 'prepare' : dt < .08 ? 'attack' : 'recover' : dt < -.15 ? 'prepare' : dt < .16 ? outgoing.attack?.kind === 'spirit' ? 'cast' : 'attack' : 'recover'
  } else if (incoming) {
    const hit = incoming.attack!
    pose = hit.outcome === 'dodge' ? 'dodge' : hit.blocked || hit.guarded || hit.outcome === 'parry' ? 'guard' : hit.applied > 0 ? 'hit' : 'idle'
  } else if (events.some(e => e.actor === actor.id && !e.attack && t >= e.at - .15 && t < e.at + .65 && (e.tone === 'magic' || e.tone === 'heal'))) pose = 'cast'
  else if (events.some(e => e.actor === actor.id && e.tone === 'rest' && t >= e.at && t < e.at + .7)) pose = 'rest'
  else if (actor.statuses.some(s => s.id === 'stun' || s.id === 'trip')) pose = 'stagger'
  const recoil = !reduced && incoming && (incoming.attack!.applied > 0 || incoming.attack!.outcome === 'dodge')
    ? Math.sin(Math.max(0, Math.min(1, (t - incoming.at) / .32)) * Math.PI) : 0
  return { pose, outgoing: reduced ? undefined : outgoing, incoming: reduced ? undefined : incoming, recoil }
}
